<template>
  <div class="c-project__versions-component-title c-heading c-heading__title-container u-mbxs">
    <a id="project-versions-anchor" class="c-project__menu-anchor" />
    <h2 class="c-heading__title">{{ t('versions') }}</h2>
    <template v-if="project.permissions.edit">
      <div v-if="isEditVersions" class="c-project__icon c-project__icon-cancel cancel-icon-versions" @click="toggleVersionForm(true)">
        <SVGIcon class="c-button__icon" name="cancel-black" />
      </div>
      <!-- <div v-if="isEditVersions" class="c-project__icon c-project__icon-confirm confirm-icon-versions" @click="toggleVersionForm"><span class="fa-solid fa-sm fa-check" /></div> -->
      <div v-if="!isEditVersions" class="c-project__icon c-project__icon-edit edit-icon-versions" @click="toggleVersionForm(true)">
        <SVGIcon class="c-button__icon" name="pen" />
      </div>
    </template>
  </div>
  <!-- <div v-if="isDevMode ? !isAuthenticated : !isLogged" class="c-version__login-button u-mts u-mbs">
    <a :href="loginURL" class="c-version__assets-download-button">
      <button type="button" class="c-button c-button--large">
        <span class="c-button__label u-mrs">{{ t('login') }}</span>
        <i class="c-version__assets-download-icon fa fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
      </button>
    </a>
  </div> -->
  <div ref="projectVersionsContainer" class="c-project__versions-component u-mbs">
    <template v-if="isEditVersions && isNewVersion && project.permissions.edit">
      <ProjectVersionForm
        v-model="newVersion"
        :project="project"
        :is-internal-user="isInternalUser"
        :error="createVersion.err"
        :loading="createVersion.loading"
        @canceled="toggleVersionForm(true)"
        @submit="createVersion.exec"
      />
    </template>
    <template v-if="isEditVersions && !isNewVersion && (versions.length > 0 && project.permissions.edit)">
      <!-- Edit versions -->
      <ProjectVersion
        v-for="version of taggedVersion"
        :key="version.pk"
        :version-id="version.pk"
        :project="project"
        :is-edit-all-versions="isEditVersions"
        :is-vip-user="isVipUser"
        :is-internal-user="isInternalUser"
        :is-admin-user="isAdminUser"
        :is-project-owner="isSameUser"
        @add-new-version="addNewVersion"
        @add-new-asset-file="addNewAssetFile"
        @add-new-asset-url="addNewAssetUrl"
        @update-versions-order="updateVersionsOrder"
        @update-versions-tagged="updateVersionsTagged"
        @update-assets-order="updateAssetsOrder"
        @canceled="onCancelVersion"
        @updated="onUpdateVersion"
        @deleted-version="onDeleteVersion(version.pk)"
        @deleted-asset="onDeleteAsset"
      />
    </template>
    <template v-if="!isEditVersions && (versions.length > 0)">
      <template v-if="versionsOrderedList">
        <template v-for="(version, index) of versions">
          <!-- Tagged version -->
          <template v-if="versionsOrderedList[index]">
            <ProjectVersion
              v-if="versionsOrderedList[index].is_tagged && (versionsOrderedList[index].status == '1')"
              :key="version"
              :version-id="version"
              :version-index="index"
              :project="project"
              :is-tagged="true"
              :is-premium-project="isPremiumProject"
              :is-premium-user="isPremiumUser"
              :is-vip-user="isVipUser"
              :is-internal-user="isInternalUser"
              :is-admin-user="isAdminUser"
              :is-project-owner="isSameUser"
              @deleted-version="onDeleteVersion(version)"
              @deleted-asset="onDeleteAsset"
            />
          </template>
        </template>
        <div v-if="versions.length > 1 && taggedVersion[0]" class="c-project__other-versions-container">
          <div class="c-project__other-versions-title-container">
            <h1 class="c-project__other-versions-title">Other versions</h1>
            <button v-if="(versions.length > maxVisibleVersions)" class="c-project__other-versions-more u-mbs" type="button" @click="showOtherVersions">
              <i class="fa-solid fa-chevron-down" />
            </button>
          </div>
          <!-- Other versions -->
          <template v-for="(version, index) of versions">
            <ProjectVersion
              v-if="!versionsOrderedList[index].is_tagged && (versionsOrderedList[index].status == '1')"
              :key="version"
              :version-id="version"
              :version-index="index"
              :project="project"
              :is-tagged="false"
              :is-premium-project="isPremiumProject"
              :is-premium-user="isPremiumUser"
              :is-vip-user="isVipUser"
              :is-admin-user="isAdminUser"
              :is-internal-user="isInternalUser"
              :is-project-owner="isSameUser"
              @deleted-version="onDeleteVersion(version)"
              @deleted-asset="onDeleteAsset"
            />
          </template>
          <!-- Other versions (hidden) -->
          <!-- <template v-for="(version, index) of versions.slice(1, maxOtherVersions)">
            <span v-if="!project.releases[index].is_tagged" class="other-versions-hidden u-mtm u-hide">
              <ProjectVersion
                :key="version"
                :version-id="version"
                :project="project"
                @deleted-version="onDeleteVersion(version)"
                @deleted-asset="onDeleteAsset"
              />
            </span>
          </template> -->
        </div>
      </template>
    </template>
    <template v-if="!isEditVersions && (!versions.length && project.permissions.edit)">
      <p class="u-color-disabled u-mts">
        <small>{{ !isEditVersions && project.permissions.edit ?
          t('createFirstVersion') :
          ''
        }}</small>
      </p>
    </template>
    <template v-if="!versions.length && !project.permissions.edit">
      <p class="u-color-disabled u-mts">
        <small>{{ t('noVersion') }}</small>
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref } from 'vue'
import { useI18n } from '@/utils/i18n'
import {
  Version,
  VersionList,
  useRequest,
  getProject,
  getProjectVersion,
  getProjectTaggedVersion,
  createProjectVersion,
  editProjectVersionOrder,
  editVersionAsset,
  editVersionAssetsList
} from '@/utils/api'
import ProjectVersion from '@/components/ProjectVersion.vue'
import ProjectVersionForm, { VersionForm, AssetForm } from '@/components/ProjectVersionForm.vue'
// import { useAuth } from '@/utils/auth'
// import { useAuthURL } from '@/utils/urls'
import { getCookie } from '@/utils/get-cookie'
import { sortPks } from '@/utils/sort'
import { asyncFilter } from '@/utils/filter'
import SVGIcon from '@/components/SVGIcon.vue'

const { t } = useI18n({
  messages: {
    en: {
      createFirstVersion: 'Create your first version by clicking the edit button above.',
      noVersion: "There aren't any versions yet.",
      versions: 'Versions',
      login: 'Login'
    },
    fr: {
      createFirstVersion: ' Créez votre première version en cliquant sur le bouton d\'édition ci-dessus.',
      noVersion: 'Aucune version pour le moment.',
      versions: 'Versions',
      login: 'Se connecter'
    }
  }
})

const props = defineProps<{
  projectId: number
  projectDocumentation?: string
  requestUser: string
  isAuthenticated: boolean
  isPremiumProject?: boolean
  isPremiumUser?: boolean
  isVipUser?: boolean
  isInternalUser?: boolean
  isAdminUser?: boolean
}>()

const emit = defineEmits<{
  (e: 'rerender'): void
}>()

const project = ref(await getProject(props.projectId))
await project.value.releases.sort(sortPks)
if (props.isAdminUser) {
  console.log('PROJECT: ', project.value)
  // console.log('PROJECT RELEASES SORTED: ', project.value.releases)
}

const versionsPks: Ref<number[]> = ref(project.value.releases.map(r => r.pk))
// console.log('VERSIONS PKs: ', versionsPks.value)
const versionsOrder = ref(project.value.releases.map(r => r.release_order))
// await versionsOrder.value.sort()
// console.log('VERSIONS ORDERS: ', versionsOrder.value)

// map versions orders
const updatedVersionsOrders: number[] = []
for (let i = 0; i < project.value.releases.length; i++) {
  const index = versionsOrder.value.indexOf(i) !== -1 ? versionsOrder.value.indexOf(i) : i
  updatedVersionsOrders.push(versionsPks.value[index]!)
}
// console.log('UPDATED VERSIONS PKs: ', updatedVersionsOrders)

// const versions = ref<number[]>(project.value.releases.map(r => r.pk))
// const versions = ref<number[]>(versionsPks.value)
const versions = ref<number[]>(updatedVersionsOrders)
if (props.isAdminUser) {
  console.log('VERSIONS: ', versions.value)
}

const taggedVersion = ref(await getProjectTaggedVersion(props.projectId))
if (!taggedVersion.value[0]) {
  taggedVersion.value.push(project.value.releases[0])
}
// console.log('TAGGED VERSION: ', taggedVersion.value)

const projectGeneralContainer = ref(document.querySelector('.c-project__general-container'))

const projectVersionsContainer = ref<HTMLElement>()

const isSameUser = ref(project.value.owner.username === props.requestUser)
// const isAuthenticated = ref(props.isAuthenticated)

// const { isLogged } = useAuth()
// const { loginURL } = useAuthURL()

const maxVisibleVersions = 3

const isEditVersions = ref(false)
const isNewVersion = ref(false)

function toggleVersionForm (isFirstVersion?: boolean) {
  isEditVersions.value = !isEditVersions.value

  if (isFirstVersion) {
    if (!versions.value.length) {
      isNewVersion.value = true
    } else {
      isNewVersion.value = false
    }
  } else if (!isFirstVersion) {
    isEditVersions.value = true
    isNewVersion.value = true
  }

  if (!isEditVersions.value) {
    emit('rerender')
  }
}

// versions order
const versionsOrderedList: Version[] = []
for (let i = 0; i < versions.value.length; i++) {
  const v = await getProjectVersion(versions.value[i])
  versionsOrderedList.push(v)
}

const isTaggedValues: Boolean[] = []
for (let i = 0; i < versionsOrderedList.length; i++) {
  const val: boolean = versionsOrderedList[i].is_tagged!
  isTaggedValues.push(val)
}
const taggedValue = isTaggedValues.filter(t => t === true)
if (!taggedValue.length) {
  if (versionsOrderedList[0]) {
    versionsOrderedList[0].is_tagged = true
  }
}

// console.log('VERSIONS ORDERED LIST: ', versionsOrderedList)

// const orders: Ref<number[]> = ref([])
// const tempVersionsOrder = ref(project.value.releases)

// function setVersionsInitialOrder () {
//   const versionsInOrder: Ref<ProjectRelease[]> = ref([])

//   for (const index in versions.value) {
//     const order = tempVersionsOrder.value[parseInt(index)].release_order
//     orders.value.push(order)

//     versionsInOrder.value[order] = tempVersionsOrder.value[parseInt(index)]
//   }

//   tempVersionsOrder.value = versionsInOrder.value

//   versions.value = tempVersionsOrder.value.map(r => r.pk)
// }

// setVersionsInitialOrder()

function showOtherVersions (e: Event) {
  const target = <HTMLElement>e.currentTarget
  const icon = target.getElementsByTagName('i')[0]
  icon.classList.toggle('fa-chevron-down')
  icon.classList.toggle('fa-chevron-up')

  const hiddenVersionsElements = document.querySelectorAll('.c-project__version-component-hidden')
  for (let i = 0; i < hiddenVersionsElements.length; i++) {
    const element = <HTMLElement>hiddenVersionsElements[i]
    element.classList.toggle('u-hide')
    element.classList.toggle('u-show')
  }
}

const getVersionsLastOrder = () => {
  let lastOrder = 0

  project.value.releases.forEach((element) => {
    if (lastOrder < element.release_order) {
      lastOrder = element.release_order
    }
  })

  return lastOrder
}

// const hasTaggedVersion = (pr: ProjectRelease) => pr.is_tagged === true

// create version
const initialNewVersion = () => ({
  name: '',
  description: '',
  versionNumber: '',
  datePublished: '',
  status: '1',
  releaseNotes: '',
  releaseOrder: null,
  assets: [],
  assetName: '',
  assetDescription: '',
  assetNotes: '',
  assetPlatform: '4',
  assetIsPremium: project.value.is_premium
  // assetUnlockUrl: project.value.protection_unlock_url ? project.value.protection_unlock_url : ''
})

const newVersion = ref<VersionForm>(initialNewVersion())

const createVersion = useRequest(async () => {
  // // add asset url
  // const accessUrlElement = ref(document.querySelector('#asset-upload-link'))
  // if (accessUrlElement.value) {
  //   // if has url
  //   const url = accessUrlElement.value.innerHTML
  //   console.log('url: ', url)
  //   if(url.length) {
  //     // add url through assets api
  //     const createdVer = ref(await getProjectVersion(created.release_pk))
  //     const newAsset = createdVer.value.assets[createdVer.value.assets.length - 1]

  //     const body = {
  //       name: newAsset.name,
  //       acces_url: newAsset.access_url
  //     }
  //     await editAssetAccessUrl(newAsset.pk, body)
  //   }
  // }

  // if asset is file
  if (newVersion.value.assets.length) {
    // update all assets if there is more than one
    if (newVersion.value.assets.length > 1) {
      newVersion.value.assets.forEach(async asset => {
        const body = {
          name: asset.filename,
          label: newVersion.value.assetDescription,
          notes: newVersion.value.assetNotes,
          platform: newVersion.value.assetPlatform,
          is_premium: newVersion.value.assetIsPremium
        }

        await editVersionAsset(asset.pk, body)
      })
    } else {
      // edit only one asset
      const assetToEdit = newVersion.value.assets[newVersion.value.assets.length - 1]
      // if has file
      if (assetToEdit.size > 0) {
        const body = {
          name: newVersion.value.assetName!,
          label: newVersion.value.assetDescription,
          notes: newVersion.value.assetNotes,
          platform: newVersion.value.assetPlatform,
          is_premium: newVersion.value.assetIsPremium
          // unlock_url: newVersion.value.assetUnlockUrl
        }

        await editVersionAsset(assetToEdit.pk, body)
      }
    }
  }

  // if asset is url
  const accessUrlElement = ref(document.querySelector('#asset-upload-link') as HTMLInputElement)
  if (accessUrlElement.value) {
    // if has url
    if (accessUrlElement.value.value.length) {
      accessUrl.value = checkLinkProtocol(accessUrlElement.value.value)
      await upload.exec()
    }
  }

  // force to add asset if no file uploaded or url
  if (!newVersion.value.assets.length || !accessUrlElement.value) {
    // scroll to assets container
    const assetsFieldElement = ref(document.querySelector('.c-project__assets-form'))
    const assetsWarningElement = ref(document.querySelector('.c-project__assets-form .c-message--warning'))

    if (assetsFieldElement.value) {
      assetsFieldElement.value.scrollIntoView({ behavior: 'smooth' })

      if (assetsWarningElement.value) {
        assetsWarningElement.value.classList.remove('u-hide')
        assetsWarningElement.value.classList.remove('u-show')

        return
      }
    }
  }

  // Reformat date to YYYY-MM-DDT00:00:00+02:00
  // let dateReformatted

  if (newVersion.value.datePublished) {
    // dateReformatted = newVersion.value.datePublished.replaceAll('/', '-').concat('T00:00:00+02:00')
  } else {
    newVersion.value.datePublished = new Date().toISOString().split('T')[0]
    // dateReformatted = new Date().toISOString().split('T')[0]
  }

  const created = await createProjectVersion({
    name: newVersion.value.name,
    description: newVersion.value.description,
    version_number: newVersion.value.versionNumber,
    date_published: newVersion.value.datePublished,
    status: newVersion.value.status,
    release_notes: newVersion.value.releaseNotes,
    release_order: null,
    is_tagged: false,
    assets: newVersion.value.assets.map(a => a.pk),
    project_id: project.value.pk.toString()
  })
  // update project value
  project.value = await getProject(props.projectId)
  // reset form
  newVersion.value = initialNewVersion()
  // preprend to release list
  versions.value.unshift(created.release_pk)

  // update versions list order after new version creation
  const v = await getProjectVersion(created.release_pk)

  const createdVer = {
    pk: v.pk,
    name: v.name,
    description: v.description,
    release_order: v.release_order,
    is_tagged: v.is_tagged
  }

  versionsList.value.unshift(createdVer)

  for (const index in versionsList.value) {
    const body = {
      name: versionsList.value[parseInt(index)].name,
      description: versionsList.value[parseInt(index)].description ?? '',
      release_order: parseInt(index)
    }
    await editProjectVersionOrder(versionsList.value[parseInt(index)].pk, body)
  }

  // update to rerender
  onUpdateVersion()

  // // toggle edit form
  // isEditVersions.value = false
  // // scroll to versions container
  // if (projectVersionsContainer.value) {
  //   projectVersionsContainer.value.scrollIntoView({ behavior: 'smooth' })
  // }
})

// asset upload
const csrftoken = getCookie('csrftoken')!

const accessUrl = ref('')

// bypass the nginx proxy and upload asset url to Forum CMS directly
const upload = useRequest(async () => {
  const fd = new FormData()
  fd.append('file', '')
  fd.append('name', newVersion.value.assetName!)
  fd.append('label', newVersion.value.assetDescription!)
  fd.append('notes', newVersion.value.assetNotes!)
  fd.append('platform', newVersion.value.assetPlatform!)
  fd.append('is_premium', String(newVersion.value.assetIsPremium!))
  // fd.append('unlock_url', newVersion.value.assetUnlockUrl!)
  fd.append('url', accessUrl.value)
  fd.append('access_url', accessUrl.value)

  const url = '/api/project-assets/'

  await fetch(url, {
    credentials: 'include',
    method: 'POST',
    mode: 'same-origin',
    headers: {
      'X-CSRFToken': csrftoken
    },
    body: fd
  }).then(async response => {
    if (response.status === 200) {
      await response.json().then(async data => {
        const assetCreated = {
          pk: data.pk,
          order: data.order,
          name: data.name,
          filename: data.name,
          size: data.size,
          content_type: data.type,
          file: data.file,
          path: data.path,
          url: data.url,
          access_url: data.access_url,
          hash: data.hash,
          uuid: data.uuid,
          label: data.label,
          platform: data.platform,
          is_premium: data.is_premium,
          authorized_download: data.authorized_download,
          // unlock_url: data.unlock_url,
          asset_meta: data.asset_meta
        }

        console.log('created asset: ', assetCreated)

        newVersion.value.assets.push(assetCreated)
      })
    } else {
      response.text().then(text => {
        throw new Error(text)
      })
    }
  }).catch(error => {
    console.log(error)
  }).finally(() => {
    // assets.push(asset)
    // console.log('assets: ', assets)

    // emit('updateAssets', assets)
  })
}, {
  error () {
    // emit('updateStatus', 'error')
  }
})

function checkLinkProtocol (text: string) {
  if (text.includes('http')) {
    return text
  } else {
    const linkText = 'http://' + text
    return linkText
  }
}

const versionsList = ref<VersionList[]>([])

async function addNewVersion (list: VersionList[]) {
  project.value = await getProject(props.projectId)

  versionsList.value = list

  toggleVersionForm(false)
}

async function addNewAssetFile () {
  project.value = await getProject(props.projectId)

  // toggleVersionForm(false)
}

async function addNewAssetUrl (f: AssetForm, version: Version) {
  // upload new url asset data
  const accessUrlElement = ref(document.querySelector('#asset-upload-link') as HTMLInputElement)
  if (accessUrlElement.value) {
    // if has url
    if (accessUrlElement.value.value.length) {
      accessUrl.value = checkLinkProtocol(accessUrlElement.value.value)
      // console.log('url: ', accessUrl.value)
      await upload.exec()

      // list all version assets pks
      const versionAssets: number[] = []
      for (const index in newVersion.value.assets) {
        versionAssets.push(newVersion.value.assets[index].pk)
      }

      // add new url asset to version AssetGroup
      const versionBody = {
        name: version.name,
        description: version.description!,
        assets: versionAssets,
        assets_group: version.assets[0].asset_group!
      }
      // add new file asset to version AssetGroup
      await editVersionAssetsList(version.pk, versionBody)

      // add new asset metadata
      const assetBody = {
        name: f.name!,
        label: f.label,
        notes: f.notes,
        platform: f.platform,
        is_premium: f.isPremium ? f.isPremium : (project.value.is_premium ? project.value.is_premium : false)
        // unlock_url: f.unlockUrl ? f.unlockUrl : (project.value.protection_unlock_url ? project.value.protection_unlock_url : '')
      }

      const assetToEdit = newVersion.value.assets[newVersion.value.assets.length - 1]
      await editVersionAsset(assetToEdit.pk, assetBody)

      // update to rerender
      onUpdateVersion()

      // // project.value = await getProject(props.projectId)
      // // toggleVersionForm(false)
    }
  }
}

async function updateVersionsOrder () {
  // // /!\ update project /!\
  // project.value = await getProject(props.projectId)

  // versions.value = project.value.releases.map(r => r.pk)
  // // console.log('versions: ', versions.value)
}

async function updateVersionsTagged () {
  // versions.value = versionsList.map(r => r.pk)
  // console.log('versions: ', versions.value)
  // console.log('versionsList: ', versionsList)

  // /!\ update project /!\
  project.value = await getProject(props.projectId)
  // console.log(project.value.releases)

  // update versions
  // versions.value = project.value.releases.map(r => r.pk)
}

async function updateAssetsOrder () {

}

function onCancelVersion () {
  // toggle version form
  isEditVersions.value = false

  emit('rerender')

  if (projectGeneralContainer.value) {
    projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
  }
}

async function onUpdateVersion () {
  // update project
  // project.value = await getProject(props.projectId)
  // console.log('updated project: ', project.value)

  // toggle version form
  isEditVersions.value = false

  emit('rerender')
  // console.log('rerender')
  if (projectGeneralContainer.value) {
    projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
  }
}

async function onDeleteVersion (versionId: number) {
  // update project
  project.value = await getProject(props.projectId)

  // update versions
  versions.value = project.value.releases.map(r => r.pk)
  // remove version from versions array
  versions.value = await asyncFilter(versions.value, async (r) => {
    await new Promise(resolve => setTimeout(resolve, 10))
    return r !== versionId
  })

  // // check if deleted version was tagged
  // if (project.value.releases.every(hasTaggedVersion)) {
  //   // do nothing
  // } else {
  //   // tag last version if there is one
  //   const versionToTag = ref(project.value.releases[project.value.releases.length - 1])
  //   if (versionToTag.value) {
  //     const body = {
  //       name: versionToTag.value.name,
  //       description: versionToTag.value.description,
  //       is_tagged: true
  //     }

  //     await editProjectVersionTagged(versionToTag.value.pk, body)

  //     // update project to update tagged version
  //     project.value = await getProject(props.projectId)
  //   }
  // }

  project.value = await getProject(props.projectId)

  const versionsLastOrder = getVersionsLastOrder()
  // check if versions are in linear order
  if (versionsLastOrder + 1 > project.value.releases.length) {
    // decrease all versions order by one if not the case
    for (const index in project.value.releases) {
      const body = {
        name: project.value.releases[parseInt(index)].name,
        description: project.value.releases[parseInt(index)].description,
        release_order: project.value.releases[index].release_order > 0 ? project.value.releases[index].release_order - 1 : 0
      }
      await editProjectVersionOrder(project.value.releases[index].pk, body)
    }
    // update project to update versions order
    project.value = await getProject(props.projectId)
  }

  // // toggle version form
  // isEditVersions.value = false

  emit('rerender')

  if (projectGeneralContainer.value) {
    projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
  }
}

async function onDeleteAsset () {
  // update project
  project.value = await getProject(props.projectId)

  // update assets
  versions.value = project.value.releases.map(r => r.pk)

  // const assets =  (await getProjectVersion(parseInt(version))).assets
  // console.log('check if asset is deleted: ', assets)

  // // toggle version form
  // isEditVersions.value = false

  emit('rerender')

  if (projectGeneralContainer.value) {
    projectGeneralContainer.value.scrollIntoView({ behavior: 'smooth' })
  }
}
</script>
